import React, { useState } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { Button, Dialog, DialogContent, DialogActions, TextField, Divider, Select, MenuItem } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const styles = theme => ({
  formHeader: {
    paddingLeft: `${theme.spacing.unit * 3}px`
  },
  formGroup: {
    width: '100%',
    display: 'flex',
    padding: `${theme.spacing.unit}px 0`
  },
  label: {
    margin: 'auto 0',
    width: 120
  },
  input: {
    width: 400
  },
  cardDateInput: {
    width: 50
  },
  bigSlash: {
    fontSize: 25
  },
  bottomButtonGroup: {
    margin: `0 ${theme.spacing.unit * 3}px ${theme.spacing.unit * 3}px 0`
  }
})

// indexes of chars in CC number, where the spaces should be
const CREDIT_CARD_SPACE_INDEXES = [4, 9, 14]

// available variants for expiration month select
// will not allow to input an already expired date
const PREPATE_MONTH_VARIANTS = yearValue => {
  const monthVariants = [ '01', '02', '03', '04', '05', '06',
    '07', '08', '09', '10', '11', '12' ]
  if (dayjs().year() === yearValue) {
    return monthVariants.slice(dayjs().month() + 1)
  }
  return monthVariants
}

// available variants for expiration year input
const YEAR_VARIANTS = [
  dayjs().year(),
  dayjs().add(1, 'year').year(),
  dayjs().add(2, 'year').year(),
  dayjs().add(3, 'year').year(),
  dayjs().add(4, 'year').year(),
  dayjs().add(5, 'year').year()
]

const AddCreditCardDialog = ({ classes, open, onClose, onSubmit }) => {
  const { t } = useTranslation()

  // holds the value of the card number input
  const [cardNumber, setCardNumber] = useState('')
  // card number validation function
  const handleCardNumberChange = event => {
    let input = event.target.value
    input = input.replace(/\D/g, '') // removing all non-digit chars

    for (let i of CREDIT_CARD_SPACE_INDEXES) { // inserting spaces
      if (i < input.length) {
        input = input.substring(0, i) + ' ' + input.substring(i)
      } else break
    }

    if (input.length > 18) { // blocking extra input
      input = input.substring(0, 19)
    }

    setCardNumber(input)
  }

  // holds the value of the cardholder name input
  const [cardholderName, setCardholderName] = useState('')
  const handleCardholderNameChange = event => setCardholderName(event.target.value)

  // holds the value of the expiration month input
  const [expirationMonth, setExpirationMonth] = useState('')
  const handleExpirationMonthChange = event => setExpirationMonth(event.target.value)

  // holds the value of the expiration year input
  const [expirationYear, setExpirationYear] = useState('')
  const handleExpirationYearChange = event => setExpirationYear(event.target.value)

  const handleSubmit = () => {
    onSubmit(
      cardNumber.replace(/[ ]/g, ''), // removing spaces from the card number
      `${expirationMonth}/${String(expirationYear).substring(2)}`, // formatting to MM/yy
      cardholderName
    )
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true}>
      <h2 className={classes.formHeader}>{t('Cabinet:add credit card')}</h2>
      <Divider variant='fullWidth' />

      <DialogContent>

        {/* Card number input */}
        <div className={classes.formGroup}>
          <label className={classes.label}>{t('Cabinet:card number')}</label>
          <TextField className={classes.input} variant='outlined'
            value={cardNumber} onChange={handleCardNumberChange} />
        </div>

        {/* Cardholder name input */}
        <div className={classes.formGroup}>
          <label className={classes.label}>{t('Cabinet:cardholder name')}</label>
          <TextField className={classes.input} variant='outlined'
            value={cardholderName} onChange={handleCardholderNameChange} />
        </div>

        {/* Expiration date picker */}
        <div className={classes.formGroup}>
          <label className={classes.label}>{t('Cabinet:valid through')}</label>

          <div className={classes.input}>
            {/* Expiration month select */}
            <Select id='expiration-month'
              className={classes.durationSelect}
              value={expirationMonth}
              onChange={handleExpirationMonthChange}>
              {PREPATE_MONTH_VARIANTS(expirationYear).map(month => (
                <MenuItem value={month} key={`expM${month}`}>{month}</MenuItem>
              ))}
            </Select>

            <label className={classes.bigSlash}> / </label>

            {/* Expiration year select */}
            <Select id='expiration-year'
              className={classes.durationSelect}
              value={expirationYear}
              onChange={handleExpirationYearChange}>
              {YEAR_VARIANTS.map(year => (
                <MenuItem value={year} key={`expY${year}`}>{year}</MenuItem>
              ))}
            </Select>

          </div>
        </div>

      </DialogContent>

      {/* Bottom button group */}
      <DialogActions className={classes.bottomButtonGroup}>
        {/* Save changes button */}
        <Button onClick={handleSubmit} variant='contained' color='primary'>
          {t('Cabinet:save changes')}
        </Button>

        {/* Close dialog button */}
        <Button onClick={onClose} variant='contained'>
          {t('Cabinet:close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withStyles(styles)(AddCreditCardDialog)
